import { css } from "@emotion/react";
import { FC, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router";

import { LayoutGrid, List } from "@livechat/design-system-icons";
import { Icon, SearchInput, SegmentedControl, SpacingToken } from "@livechat/design-system-react-components";

import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import { SettingsTable } from "components/settings-table";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { type IHighlightedRule, useHighlightedRule } from "hooks/use-highlighted-rule";
import { IAutomationViewMode } from "interfaces/automations";
import { AutomationsRoutes } from "routes/automations/constants";
import { AutomationsSearchEmptyState } from "routes/automations/shared/components/AutomationsSearchEmptyState";
import { useLocalStorageSync } from "services/local-storage";
import { IRule } from "store/entities/rules/interfaces";
import { withVar } from "themes/helpers";

import { WorkflowCard } from "./WorkflowCard";
import { WorkflowsGrid } from "./styled";

interface IProps {
  rules: IRule[];
}

export const RuleCollection: FC<IProps> = ({ rules }) => {
  const history = useHistory();
  const highlightedRule = useHighlightedRule();

  const params = new URLSearchParams(history.location.search);

  const [query, setQuery] = useState(params.get("search") || "");

  // Clear a search filter that could be hiding the highlighted rule.
  useEffect(() => {
    if (highlightedRule.ruleId) {
      setQuery("");
    }
  }, [highlightedRule.ruleId, highlightedRule.requestKey]);

  const [viewMode, setViewMode] = useLocalStorageSync("rulesViewMode", "grid" as IAutomationViewMode);

  const handleViewModeChange = (selectedMode: IAutomationViewMode): void => {
    trackEngagement("Rules: View changed", EventPlace.Automations, {
      currentView: selectedMode,
      previousView: viewMode,
    });
    setViewMode(selectedMode);
  };

  const handleQueryChange = (query: string): void => {
    setQuery(query);
    params.set("search", query);
    history.replace(`${AutomationsRoutes.Rules}?${params.toString()}`);
    trackEngagement("Rules: Search performed", EventPlace.Automations, {
      currentView: viewMode,
    });
  };

  const filteredRules = useMemo(() => {
    if (!query) {
      return rules;
    }

    const normalizedQuery = query.toLowerCase();

    return rules.filter(
      rule =>
        rule.name.toLowerCase().includes(normalizedQuery) || rule.description.toLowerCase().includes(normalizedQuery)
    );
  }, [rules, query]);

  return (
    <Flex column>
      <Flex justifyContent="space-between">
        <SearchInput
          size="compact"
          value={query}
          onChange={handleQueryChange}
          placeholder="Search…"
          css={{ width: "auto" }}
        />
        <SegmentedControl
          buttons={[
            { id: "grid", icon: <Icon source={LayoutGrid} /> },
            { id: "list", icon: <Icon source={List} /> },
          ]}
          currentId={viewMode}
          size="compact"
          onButtonClick={id => handleViewModeChange(id as IAutomationViewMode)}
        />
      </Flex>
      <Block>
        {filteredRules.length ? (
          <RuleDisplay rules={filteredRules} displayMode={viewMode} highlightedRule={highlightedRule} />
        ) : (
          <AutomationsSearchEmptyState
            title="No rules found"
            description="Adjust search criteria or show all rules"
            primaryAction={{ label: "Show all rules", onClick: () => handleQueryChange("") }}
          />
        )}
      </Block>
    </Flex>
  );
};

interface IRuleDisplayProps {
  rules: IRule[];
  displayMode: IAutomationViewMode;
  highlightedRule: IHighlightedRule;
}

const RuleDisplay: FC<IRuleDisplayProps> = ({ rules, displayMode, highlightedRule }) => {
  const [hasScrolled, setHasScrolled] = useState(false);

  // Re-arm scrolling for a repeat highlight of the same rule.
  useEffect(() => {
    setHasScrolled(false);
  }, [highlightedRule.ruleId, highlightedRule.requestKey]);

  const handleScroll = (): void => {
    setHasScrolled(true);
  };

  if (displayMode === "list") {
    return (
      <SettingsTable
        marginTop={withVar(SpacingToken.Spacing4)}
        marginBottom="0"
        columns={6}
        columnSizes="min-content 2fr 2fr 1fr 1fr min-content"
        headers={["Status", "Name", "Description", "Teams", "Used", ""]}
        data-testid="workflows-table"
      >
        {rules.map(rule => (
          <WorkflowCard
            key={rule.id}
            rule={rule}
            displayMode="list"
            highlight={highlightedRule.ruleId === rule.id}
            hasScrolled={hasScrolled}
            onScroll={handleScroll}
          />
        ))}
      </SettingsTable>
    );
  }

  return (
    <WorkflowsGrid data-testid="workflows-grid">
      {rules.map(rule => (
        <WorkflowCard
          key={rule.id}
          rule={rule}
          displayMode="grid"
          highlight={highlightedRule.ruleId === rule.id}
          hasScrolled={hasScrolled}
          onScroll={handleScroll}
          css={
            rules.length === 1
              ? css`
                  max-width: 540px;
                `
              : undefined
          }
        />
      ))}
    </WorkflowsGrid>
  );
};
